import React, { useState, useEffect } from 'react'
import '../styles/Orders.css'

const BASE_URL = 'https://api.admin.yourhood.ge'
const PAGE_SIZE = 15

export default function Orders() {
  const [orders, setOrders]               = useState([])
  const [isLoading, setIsLoading]         = useState(false)
  const [error, setError]                 = useState('')
  const [page, setPage]                   = useState(1)
  const [totalPages, setTotalPages]       = useState(1)
  const [filterShipped, setFilterShipped] = useState('all')
  const [expandedId, setExpandedId]       = useState(null)
  const [orderItems, setOrderItems]       = useState({})
  const [loadingItems, setLoadingItems]   = useState({})

  const token = localStorage.getItem('token')
  const authHeaders = { 'X-Language': 'ka', 'Authorization': token }

  const filterStatusParam = () => {
    if (filterShipped === 'pending') return '&filterstatus=0'
    if (filterShipped === 'shipped') return '&filterstatus=1'
    return ''
  }

  const fetchOrders = async (p = 1) => {
    setIsLoading(true)
    setError('')
    setExpandedId(null)
    try {
      const res = await fetch(
        `${BASE_URL}/api/Order/orders?Page=${p}&PageSize=${PAGE_SIZE}${filterStatusParam()}`,
        { headers: authHeaders }
      )
      const data = await res.json()
      if (res.ok) {
        const list = Array.isArray(data) ? data : []
        setOrders(list)
        if (list.length === 0 && p > 1) {
          setPage(1)
          return
        }
        if (list.length < PAGE_SIZE) {
          setTotalPages(p)
        } else {
          setTotalPages(p + 1)
        }
      } else {
        setError('შეკვეთების ჩატვირთვა ვერ მოხერხდა')
      }
    } catch {
      setError('კავშირის შეცდომა')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders(page)
  }, [page, filterShipped])

  const fetchOrderItems = async (orderId) => {
    if (orderItems[orderId]) return
    setLoadingItems(prev => ({ ...prev, [orderId]: true }))
    try {
      const res = await fetch(
        `${BASE_URL}/api/Order/orderitems?OrderID=${orderId}`,
        { headers: authHeaders }
      )
      const data = await res.json()
      if (res.ok) {
        setOrderItems(prev => ({ ...prev, [orderId]: Array.isArray(data) ? data : [] }))
      }
    } catch {}
    finally {
      setLoadingItems(prev => ({ ...prev, [orderId]: false }))
    }
  }

  const toggleExpand = (orderId) => {
    if (expandedId === orderId) {
      setExpandedId(null)
    } else {
      setExpandedId(orderId)
      fetchOrderItems(orderId)
    }
  }

  const toggleSendStatus = async (order) => {
    const newStatus = order.sendStatus === 1 ? 0 : 1
    setOrders(prev =>
      prev.map(o => o.id === order.id ? { ...o, sendStatus: newStatus } : o)
    )
    try {
      const res = await fetch(
        `${BASE_URL}/api/Order/change-status?OrderID=${order.id}&status=${newStatus}`,
        { method: 'PUT', headers: authHeaders }
      )
      if (!res.ok) {
        setOrders(prev =>
          prev.map(o => o.id === order.id ? { ...o, sendStatus: order.sendStatus } : o)
        )
      }
    } catch {
      setOrders(prev =>
        prev.map(o => o.id === order.id ? { ...o, sendStatus: order.sendStatus } : o)
      )
    }
  }

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return
    setPage(p)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const getPageNumbers = () => {
    const pages = []
    const delta = 2
    const start = Math.max(1, page - delta)
    const end   = Math.min(totalPages, page + delta)
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }

  return (
    <div className="ord-wrapper">

      <div className="ord-header">
        <div>
          <h2 className="ord-title">შეკვეთები</h2>
          <p className="ord-subtitle">{orders.length} ჩანაწერი — გვერდი {page}</p>
        </div>
      </div>

      <div className="ord-filters">
        {[
          { key: 'all',     label: 'ყველა' },
          { key: 'pending', label: 'გაუგზავნელი' },
          { key: 'shipped', label: 'გაგზავნილი' },
        ].map(f => (
          <button
            key={f.key}
            className={`ord-filter-btn ${filterShipped === f.key ? 'active' : ''}`}
            onClick={() => setFilterShipped(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="ord-error">{error}</div>}

      {isLoading ? (
        <div className="ord-empty">იტვირთება...</div>
      ) : orders.length === 0 ? (
        <div className="ord-empty">შეკვეთები არ მოიძებნა</div>
      ) : (
        <>
          <div className="ord-list">
            {orders.map(order => {
              const isOpen     = expandedId === order.id
              const isShipped  = order.sendStatus === 1
              const items      = orderItems[order.id] || []
              const itemsTotal = items.reduce((s, i) => s + (i.price * i.quantity), 0)

              return (
                <div key={order.id} className={`ord-card${isShipped ? ' shipped-card' : ''}`}>
                  <div className="ord-card-summary">
                    <div className="ord-card-row" onClick={() => toggleExpand(order.id)}>
                      <div className="ord-card-left">
                        <span className="ord-id">#{order.id.slice(0, 8)}</span>
                        <div className="ord-customer-name">{order.fullname || '—'}</div>
                        <div className="ord-customer-phone">{order.phone || '—'}</div>
                      </div>
                      <div className="ord-card-middle">
                        <div className="ord-info-row">
                          <span className="ord-info-label">მისამართი</span>
                          <span className="ord-info-value">{order.adddress || '—'}</span>
                        </div>
                        <div className="ord-info-row">
                          <span className="ord-info-label">პროდუქტები</span>
                          <span className="ord-qty-badge">{order.orderItemQuantity} ცალი</span>
                        </div>
                        <div className="ord-info-row">
                          <span className="ord-info-label">გადახდა</span>
                          <span className={`ord-status-badge ${
                            order.status === 0 ? 'paid' :
                            order.status === 2 ? 'rejected' : 'unpaid'
                          }`}>
                            {order.status === 0 ? '✓ გადახდილია' :
                             order.status === 2 ? '✕ უარყოფილია' : '○ მოლოდინშია'}
                          </span>
                        </div>
                        <div className="ord-info-row">
                          <span className="ord-info-label">გაგზავნა</span>
                          <span className={`ord-status-badge ${isShipped ? 'shipped' : 'pending'}`}>
                            {isShipped ? '✓ გაგზავნილია' : '○ გაუგზავნელი'}
                          </span>
                        </div>
                      </div>
                      <div className="ord-card-right">
                        <div className="ord-shipped-wrap" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="ord-shipped-checkbox"
                            checked={isShipped}
                            onChange={() => toggleSendStatus(order)}
                            title={isShipped ? 'გაგზავნილია' : 'გასაგზავნი'}
                          />
                        </div>
                        <div className={`ord-expand-icon${isOpen ? ' open' : ''}`}>▾</div>
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="ord-details">
                      <div>
                        <div className="ord-detail-label">მისამართი</div>
                        <div className="ord-address-text">{order.adddress || '—'}</div>
                        <div className={`ord-shipped-status ${isShipped ? 'yes' : 'no'}`}>
                          {isShipped ? '✓ გაგზავნილია' : '○ გაუგზავნელი'}
                        </div>
                      </div>
                      <div>
                        <div className="ord-detail-label">პროდუქტები</div>
                        {loadingItems[order.id] ? (
                          <div className="ord-items-loading">იტვირთება...</div>
                        ) : items.length === 0 ? (
                          <div className="ord-items-loading">—</div>
                        ) : (
                          <div className="ord-products-list">
                            {items.map((item, i) => (
                              <div key={i} className="ord-product-item">
                                {item.imageUrl && (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.productName}
                                    className="ord-product-img"
                                  />
                                )}
                                <div className="ord-product-info">
                                  <div className="ord-product-top">
                                    <span>
                                      <span className="ord-product-qty">×{item.quantity}</span>
                                      <span className="ord-product-name">{item.productName}</span>
                                    </span>
                                    <span className="ord-product-price">
                                      {item.salePrice != null ? (
                                        <>
                                          <span className="ord-price-sale">{item.salePrice * item.quantity} ₾</span>
                                          <span className="ord-product-original">{item.price * item.quantity} ₾</span>
                                        </>
                                      ) : (
                                        <span>{item.price * item.quantity} ₾</span>
                                      )}
                                    </span>
                                  </div>
                                  {item.size && (
                                    <div className="ord-product-meta">ზომა: <span>{item.size}</span></div>
                                  )}
                                  {item.color && (
                                    <div className="ord-product-meta">ფერი: <span>{item.color}</span></div>
                                  )}
                                  {item.productDescription && (
                                    <div className="ord-product-desc">{item.productDescription}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                            <hr className="ord-divider" />
                            <div className="ord-total-line">
                              <span>სულ</span>
                              <span>{itemsTotal} ₾</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="ord-pagination">
            <button
              className="ord-page-btn ord-page-arrow"
              onClick={() => goToPage(page - 1)}
              disabled={page === 1 || orders.length === 0}
            >
              ←
            </button>

            {page > 3 && (
              <>
                <button className="ord-page-btn" onClick={() => goToPage(1)}>1</button>
                {page > 4 && <span className="ord-page-ellipsis">…</span>}
              </>
            )}

            {getPageNumbers().map(n => (
              <button
                key={n}
                className={`ord-page-btn ${n === page ? 'active' : ''}`}
                onClick={() => goToPage(n)}
              >
                {n}
              </button>
            ))}

            {page < totalPages - 2 && (
              <>
                {page < totalPages - 3 && <span className="ord-page-ellipsis">…</span>}
                <button className="ord-page-btn" onClick={() => goToPage(totalPages)}>{totalPages}</button>
              </>
            )}

            <button
              className="ord-page-btn ord-page-arrow"
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages || orders.length < PAGE_SIZE || orders.length === 0}
            >
              →
            </button>
          </div>
        </>
      )}
    </div>
  )
}